import React from 'react';
import { Activity, ArrowLeft, ExternalLink, Clock } from 'lucide-react';
import { useRecentTransactions } from '../hooks/useLeaderboardData';
import { formatCurrency, formatNumber } from '../utils/formatters';

const Recent: React.FC = () => {
  const { data: transactions, loading, error, refresh } = useRecentTransactions(50);

  const formatTimeAgo = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    
    return date.toLocaleDateString();
  };

  const getDirectionColor = (direction: string) => {
    return direction === 'to_shido' ? 'text-green-400' : 'text-blue-400';
  };

  const getDirectionLabel = (direction: string) => {
    return direction === 'to_shido' ? 'To Shido' : 'From Shido';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 p-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <button
            onClick={() => window.location.hash = '#/'}
            className="btn-secondary p-2 rounded-lg hover:bg-white/20 transition-all"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-3xl md:text-4xl font-bold text-white flex items-center gap-3">
              <Activity className="w-8 h-8 text-green-400" />
              Recent Activity
            </h1>
            <p className="text-slate-300 mt-2">
              Latest bridge transactions on the Shido Network
            </p>
          </div>
          <button
            onClick={refresh}
            className="btn-primary px-4 py-2 rounded-lg ml-auto"
          >
            Refresh
          </button>
        </div>

        {/* Recent Transactions */}
        <div className="glass-card p-6">
          {loading ? (
            <div className="space-y-4">
              {[...Array(10)].map((_, i) => (
                <div key={i} className="loading-shimmer h-20 rounded-lg"></div>
              ))}
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <Activity className="w-12 h-12 text-red-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-red-400 mb-2">Failed to Load</h3>
              <p className="text-slate-400 mb-4">{error}</p>
              <button
                onClick={refresh}
                className="btn-primary px-6 py-2 rounded-lg"
              >
                Try Again
              </button>
            </div>
          ) : transactions.length === 0 ? (
            <div className="text-center py-12">
              <Activity className="w-12 h-12 text-slate-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-slate-400 mb-2">No Recent Activity</h3>
              <p className="text-slate-500">
                Transactions will appear here as they happen
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-white">
                  Latest {transactions.length} Transactions
                </h2>
                <div className="text-sm text-slate-400">
                  Auto-refreshes every 15 seconds
                </div>
              </div>

              {transactions.map((tx, index) => (
                <div
                  key={tx.txHash}
                  className="flex items-center justify-between p-4 rounded-lg bg-white/5 hover:bg-white/10 transition-all group animate-slide-up"
                  style={{ animationDelay: `${index * 50}ms` }}
                >
                  <div className="flex items-center gap-4">
                    <div className="flex-shrink-0">
                      <div className={`w-3 h-3 rounded-full ${
                        tx.bridgeDirection === 'to_shido' ? 'bg-green-400' : 'bg-blue-400'
                      }`}></div>
                    </div>
                    
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span 
                          className="font-mono text-sm text-white group-hover:text-blue-300 transition-colors cursor-pointer"
                          onClick={() => window.location.hash = `/user/${tx.userAddress}`}
                        >
                          {tx.displayAddress}
                        </span>
                        <span className={`text-xs px-2 py-1 rounded-full ${getDirectionColor(tx.bridgeDirection)} bg-white/10`}>
                          {getDirectionLabel(tx.bridgeDirection)}
                        </span>
                      </div>
                      
                      <div className="flex items-center gap-4 text-sm text-slate-400">
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {formatTimeAgo(tx.timestamp)}
                        </span>
                        <span>Block #{formatNumber(tx.blockNumber)}</span>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <div className="text-lg font-bold text-white">
                        {formatNumber(parseFloat(tx.amount), 2)} {tx.token}
                      </div>
                      <div className="text-sm text-slate-400">
                        {formatCurrency(tx.amount)}
                      </div>
                    </div>
                    
                    <button
                      onClick={() => window.open(`https://shidoscan.net/tx/${tx.txHash}`, '_blank')}
                      className="btn-secondary p-2 rounded-lg opacity-0 group-hover:opacity-100 transition-all"
                      title="View on Shidoscan"
                    >
                      <ExternalLink className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Back to Home */}
        <div className="text-center mt-8">
          <button
            onClick={() => window.location.hash = '#/'}
            className="btn-secondary px-6 py-3 rounded-lg"
          >
            ← Back to Home
          </button>
        </div>
      </div>
    </div>
  );
};

export default Recent;
